import type { ReactElement } from 'react'
import { Button } from './ui/index.ts'
import { useTheme } from '../lib/theme.ts'

/**
 * The dark/light switch in the header.
 *
 * Two states, not three. A "system" option sounds tidier but means the
 * button has no stable label and an operator cannot tell by looking which
 * way the next click goes; `lib/theme.ts` already falls back to the OS
 * preference until someone makes a choice, which covers the case the third
 * state would have been for.
 *
 * Icon-only, with the label in `aria-label` and `title`. The label names
 * the DESTINATION ("Switch to light theme") rather than the current state,
 * because a control is named by what it does.
 */

export function ThemeToggle(): ReactElement {
  const { theme, toggleTheme } = useTheme()
  const label =
    theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'

  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={label}
      title={label}
      onClick={toggleTheme}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </Button>
  )
}

/* Both glyphs are `currentColor` stroke on a 16px box, so they inherit the
   Button's token colour and its hover transition instead of carrying a
   colour of their own — the one way an icon stays inside the token layer.
   `aria-hidden` because the button is already named above; an SVG that
   also announces itself reads the control out twice. */

function SunIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    >
      <circle cx="8" cy="8" r="3.1" />
      <path d="M8 1v1.6M8 13.4V15M1 8h1.6M13.4 8H15M3 3l1.2 1.2M11.8 11.8L13 13M13 3l-1.2 1.2M4.2 11.8L3 13" />
    </svg>
  )
}

function MoonIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinejoin="round"
    >
      <path d="M13.2 9.6A5.6 5.6 0 0 1 6.4 2.8a5.6 5.6 0 1 0 6.8 6.8Z" />
    </svg>
  )
}
